// [UNTAPPED-v2] Brand Card Component - Dashboard
// Shows untapped_opportunities count per brand from affiliate_clean table
// Related backend: FetchAllBrand.js (untapped_opportunities_country query)
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedBrand } from '../../store/slices/liveAffiliatesSlice';
import { setSelectedBrand as setSelectedBrandForUntapped } from '../../store/slices/untappedOpportunitiesSlice';
import {
  startPollingWithCleanup,
  stopPollingWithCleanup,
  selectBrandProcessingStatus,
  cleanupProcessingBrands
} from '../../store/slices/brandsSlice';
import { Globe, ExternalLink, Zap, Activity, Users, Clock, CheckCircle, XCircle, TrendingUp, TrendingDown, Sparkles, ChevronDown, ChevronUp, Loader2, BarChart3, ShieldAlert } from 'lucide-react';
import FaviconImage from '../common/FaviconImage';
import FlagImage from '../common/FlagImage';

const BrandCard = ({ brand }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  // Get user plan from Redux
  const user = useSelector(state => state.auth.user);
  const isFreePlan = !user?.plan || user.plan.toLowerCase() === 'free';

  // Analytics dropdown state
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [hasDetailedAnalytics, setHasDetailedAnalytics] = useState(false); // Default false, only true after confirmed
  const [analyticsChecked, setAnalyticsChecked] = useState(false); // Track if we've checked for analytics

  // Lost affiliate alert state
  const [lostAlertEnabled, setLostAlertEnabled] = useState(brand.lost_affiliate_alert || false);
  const [lostAlertLoading, setLostAlertLoading] = useState(false);
  const [lostAffiliatesOpen, setLostAffiliatesOpen] = useState(false);
  const [lostAffiliates, setLostAffiliates] = useState(null);
  const [lostAffiliatesLoading, setLostAffiliatesLoading] = useState(false);

  // API configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || 'https://mars2.mediacle.net';

  // Get processing status from Redux store using selector
  const processingInfo = useSelector(selectBrandProcessingStatus(brand.brand_url));
  
  // Use Redux store status if available, otherwise fall back to brand prop
  const currentProcessingStatus = processingInfo?.status || brand.processing_status;
  
  // Check if brand is currently processing (1-7 are processing, 8 is completed)
  const isProcessing = currentProcessingStatus && 
                      parseInt(currentProcessingStatus) >= 1 && 
                      parseInt(currentProcessingStatus) < 8 &&
                      currentProcessingStatus !== 'completed' &&
                      currentProcessingStatus !== 'failed';

  const isCompleted = currentProcessingStatus === 'completed' || parseInt(currentProcessingStatus) === 8;
  const isFailed = currentProcessingStatus === 'failed';

  // 🔍 DEBUG: Check if we have current month data
  const hasCurrentMonthData = brand.current_month_gained !== undefined || brand.current_month_lost !== undefined;
  // Prefer the actual analytics-array lengths once loaded, so the header pills
  // match the detail sections. The server-side current_month_gained/lost
  // sometimes reflects a different aggregation (e.g. backlink-derived totals)
  // and that mismatch confuses users.
  const rawGained = brand.current_month_gained || 0;
  const rawLost = brand.current_month_lost || 0;
  const loadedGained = analyticsData?.data?.gained?.length;
  const loadedLost = analyticsData?.data?.lost?.length;
  const gainedCount = (typeof loadedGained === 'number') ? loadedGained : rawGained;
  const lostCount = (typeof loadedLost === 'number') ? loadedLost : rawLost;

  // 🔍 DEBUG: Log brand data to console

  // Start polling if brand is processing
  useEffect(() => {
    if (isProcessing && !processingInfo?.isActive) {
      dispatch(startPollingWithCleanup({
        brandUrl: brand.brand_url,
        brandName: brand.brand_name || brand.name
      }));
    }

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (processingInfo?.isActive) {
        dispatch(stopPollingWithCleanup(brand.brand_url));
      }
    };
  }, [isProcessing, processingInfo?.isActive, brand.brand_url, brand.brand_name, brand.name, dispatch]);

  // Stop polling when processing completes or fails
  useEffect(() => {
    if (processingInfo?.isActive && (isCompleted || isFailed)) {
      dispatch(stopPollingWithCleanup(brand.brand_url));
    }
  }, [processingInfo?.isActive, isCompleted, isFailed, brand.brand_url, currentProcessingStatus, dispatch]);

  // Pre-fetch lost affiliates for completed brands
  useEffect(() => {
    if (isCompleted && brand.id) {
      fetch(`${API_BASE_URL}/api/live-affiliate-monitor/lost/${brand.id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => { if (data.success) setLostAffiliates(data.data); })
        .catch(() => {});
    }
  }, [isCompleted, brand.id, API_BASE_URL]);

  // Cleanup old processing records periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      dispatch(cleanupProcessingBrands());
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(cleanupInterval);
  }, [dispatch]);

  // Fetch analytics data (used for both pre-fetch and dropdown)
  const fetchBrandAnalytics = async (isPreFetch = false) => {

    if (analyticsData) {
      return;
    }

    if (!isPreFetch) {
      setAnalyticsLoading(true);
    }
    setAnalyticsError(null);

    const requestBody = {
      brandUrl: brand.brand_url || brand.url,
      fetchCurrentMonth: true
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/analyticsPerformance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalyticsData(data);

      // Check if there's actual detailed data
      const hasData = (data.data?.gained?.length > 0) || (data.data?.lost?.length > 0);
      setHasDetailedAnalytics(hasData);
      setAnalyticsChecked(true);

      // If no detailed data and not pre-fetch, close the dropdown
      if (!hasData && !isPreFetch) {
        setAnalyticsOpen(false);
      }
    } catch (err) {
      console.error('❌ Error fetching brand analytics for', brand.brand_name, ':', err);
      setAnalyticsError(err.message);
      setHasDetailedAnalytics(false);
      setAnalyticsChecked(true);
      if (!isPreFetch) {
        setAnalyticsOpen(false);
      }
    } finally {
      if (!isPreFetch) {
        setAnalyticsLoading(false);
      }
    }
  };

  // Pre-fetch analytics on mount to determine if button should show
  useEffect(() => {
    if ((gainedCount > 0 || lostCount > 0) && !analyticsChecked && !isProcessing && !isFailed) {
      fetchBrandAnalytics(true); // Pre-fetch silently
    }
  }, [gainedCount, lostCount, analyticsChecked, isProcessing, isFailed]);

  // Handle analytics dropdown toggle
  const handleAnalyticsToggle = (e) => {
    e.stopPropagation();
    const newState = !analyticsOpen;
    setAnalyticsOpen(newState);
    if (newState && !analyticsData) {
      fetchBrandAnalytics(false); // Not pre-fetch, show loading
    }
  };

  const handleLiveAffiliatesClick = () => {
    if (isProcessing) return; // Prevent clicks during processing
    
    dispatch(setSelectedBrand({
      brand_url: brand.brand_url || brand.url,
      brand_name: brand.brand_name || brand.name,
      brand_id: brand.id,
      country: brand.country,
      vertical: brand.vertical
    }));
    navigate('/live-affiliate');
  };

  // Toggle lost affiliate alert
  const handleToggleLostAlert = async (e) => {
    e.stopPropagation();
    if (lostAlertLoading || isFreePlan) return;

    setLostAlertLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/live-affiliate-monitor/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brandId: brand.id, enabled: !lostAlertEnabled })
      });
      const data = await response.json();
      if (data.success) {
        setLostAlertEnabled(data.enabled);
      }
    } catch (error) {
      console.error('Failed to toggle lost affiliate alert:', error);
    } finally {
      setLostAlertLoading(false);
    }
  };

  // Fetch lost affiliates
  const fetchLostAffiliates = async () => {
    if (lostAffiliates) return; // Already fetched
    setLostAffiliatesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/live-affiliate-monitor/lost/${brand.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setLostAffiliates(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch lost affiliates:', error);
    } finally {
      setLostAffiliatesLoading(false);
    }
  };

  const handleLostAffiliatesToggle = (e) => {
    e.stopPropagation();
    const newState = !lostAffiliatesOpen;
    setLostAffiliatesOpen(newState);
    if (newState) fetchLostAffiliates();
  };

  const handleUntappedOpportunitiesClick = () => {
    if (isProcessing) return; // Prevent clicks during processing

    // Support multiple countries - use brand.countries if available, otherwise wrap single country in array
    const brandCountries = brand.countries && Array.isArray(brand.countries) && brand.countries.length > 0
      ? brand.countries
      : (brand.country ? [brand.country] : []);

    dispatch(setSelectedBrandForUntapped({
      brand_url: brand.brand_url || brand.url,
      brand_name: brand.brand_name || brand.name,
      brand_id: brand.id,
      country: brand.country,
      countries: brandCountries,
      vertical: brand.vertical
    }));
    navigate('/untapped-opportunities');
  };

  const getProcessingDisplay = () => {
    // Use current status from Redux or brand prop
    const status = currentProcessingStatus;
    const step = processingInfo?.step;
    
    if (!status || (parseInt(status) < 1 && status !== 'completed' && status !== 'failed')) return null;
    
    const statusMessages = {
      '1': 'Initializing brand analysis',
      '2': 'Fetching data',
      '3': 'Analyzing search results',
      '4': 'Classifying affiliates',
      '5': 'Processing backlink data',
      '6': 'Analyzing organic keywords',
      '7': 'Processing paid keywords',
      '8': 'Finalizing geographic analysis',
      'completed': 'Analysis completed',
      'failed': 'Processing failed'
    };
    
    const message = step || statusMessages[status] || 'Processing';
    const stepNumber = parseInt(status) || (status === 'completed' ? 8 : (status === 'failed' ? 0 : 1));
    
    return {
      message,
      stepNumber,
      progress: status === 'completed' ? 100 : (status === 'failed' ? 0 : (stepNumber / 8) * 100),
      status
    };
  };

  const processingDisplay = getProcessingDisplay();

  const getStatusIndicator = () => {
    if (isFailed) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-3 h-3 text-red-600" />
          <span className="text-xs font-medium text-red-700">Failed</span>
        </div>
      );
    }
    
    if (isCompleted) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-3 h-3 text-green-600" />
          <span className="text-xs font-medium text-green-700">Completed</span>
        </div>
      );
    }
    
    if (isProcessing) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
          <Clock className="w-3 h-3 text-blue-600 animate-spin" />
          <span className="text-xs font-medium text-blue-700">
            Step {processingDisplay?.stepNumber || 1}/8
          </span>
        </div>
      );
    }
    
    return null;
  };

  const metrics = [
    {
      title: 'MARS Health Score',
      value: brand.mars_health_score || 'N/A',
      icon: Zap,
      onClick: null
    },
    {
      title: 'Estimated Affiliate Traffic',
      value: brand.estimated_affiliate_traffic || 'N/A',
      icon: Activity,
      onClick: null
    },
    {
      title: 'Live Affiliates Count',
      value: brand.live_affiliates_count || 0,
      icon: Users,
      onClick: handleLiveAffiliatesClick
    },
    {
      title: 'Untapped Opportunities',
      value: brand.untapped_opportunities_country || brand.untapped_opportunities || 0,
      icon: Sparkles,
      onClick: handleUntappedOpportunitiesClick
    }
  ];

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 group relative overflow-hidden transform ${
      isProcessing ? 'border-blue-300 dark:border-blue-600 shadow-sm' : 'hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm hover:-translate-y-0.5'
    }`}>
      <div className="relative p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-5">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center gap-2">
              <FaviconImage
                domain={brand.brand_url || brand.url}
                size={28}
                className="rounded-md"
              />
              {isProcessing && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </div>
            <div>
              <h3 className="text-base font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                {brand.brand_name || brand.name}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                  <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  <span>{brand.brand_url || brand.url}</span>
                  <ExternalLink
                    className="w-3 h-3 cursor-pointer text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://${brand.brand_url || brand.url}`, '_blank');
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              <FlagImage countryCode={brand.country} size={14} />
              <span className="text-xs font-medium text-gray-600 dark:text-slate-300">{brand.country?.toUpperCase()}</span>
            </div>
            <div className="px-2.5 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium">
              {brand.vertical ? brand.vertical.split(',').map(v => v.trim().charAt(0).toUpperCase() + v.trim().slice(1).toLowerCase()).join(', ') : ''}
            </div>

            {/* Processing/Status Indicator */}
            {getStatusIndicator()}
          </div>
        </div>

        {/* Processing Progress Bar */}
        {isProcessing && processingDisplay && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-blue-600">
                {processingDisplay.message}...
              </span>
              <span className="text-xs text-blue-500">
                {processingDisplay.stepNumber}/8
              </span>
            </div>
            <div className="w-full bg-blue-100 rounded-full h-1.5">
              <div 
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${processingDisplay.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Failed Status Message */}
        {isFailed && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-xs text-red-700">
              Processing failed. Please try adding the brand again.
            </div>
          </div>
        )}

        <div className="h-px bg-gray-200 dark:bg-slate-700 my-5"></div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {metrics.map((metric, metricIndex) => (
            <div
              key={metricIndex}
              className={`bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 transition-all duration-300 ${
                metric.onClick && !isProcessing && !isFailed
                  ? 'cursor-pointer transform hover:scale-105 hover:shadow-sm hover:bg-gray-100 dark:hover:bg-slate-700'
                  : ''
              } ${isProcessing || isFailed ? 'opacity-75' : ''}`}
              onClick={!isProcessing && !isFailed ? metric.onClick : undefined}
            >
              <div className="flex items-center gap-2 mb-2">
                <metric.icon className={`w-4 h-4 ${
                  isProcessing || isFailed ? 'text-gray-400 dark:text-slate-500' : 'text-blue-600 dark:text-blue-400'
                }`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-slate-200">
                      {isProcessing && (metric.title.includes('Affiliates') || metric.title.includes('Opportunities'))
                        ? 'Processing...'
                        : isFailed && (metric.title.includes('Affiliates') || metric.title.includes('Opportunities'))
                        ? 'Failed'
                        : metric.value
                      }
                    </div>

                    {/* 🎯 GAINED/LOST ARROWS - Only show for Live Affiliates Count */}
                    {metric.title === 'Live Affiliates Count' && hasCurrentMonthData && !isProcessing && !isFailed && (
                      <div className="flex items-center gap-1">
                        {gainedCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 rounded text-xs">
                            <TrendingUp className="w-3 h-3 text-green-600" />
                            <span className="text-green-700 font-medium">{gainedCount}</span>
                          </div>
                        )}
                        {lostCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 rounded text-xs">
                            <TrendingDown className="w-3 h-3 text-red-600" />
                            <span className="text-red-700 font-medium">{lostCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-xs font-medium text-gray-500 dark:text-slate-400">
                {metric.title}
              </div>
            </div>
          ))}
        </div>

        {/* Lost Affiliate Alert Toggle */}
        {isCompleted && (
          <div className="mt-4">
            <button
              onClick={handleToggleLostAlert}
              disabled={lostAlertLoading || isFreePlan}
              title={isFreePlan ? 'Upgrade your plan to enable Lost Affiliate Monitoring' : ''}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all duration-200 ${
                isFreePlan
                  ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
                  : lostAlertEnabled
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
                  : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-800'
              }`}
            >
              <div className="flex items-center gap-2.5">
                {lostAlertLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                ) : (
                  <ShieldAlert className={`w-4 h-4 ${
                    isFreePlan ? 'text-gray-300 dark:text-slate-600' : lostAlertEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-slate-500'
                  }`} />
                )}
                <div className="text-left">
                  <span className={`text-xs font-semibold ${
                    isFreePlan ? 'text-gray-400 dark:text-slate-500' : lostAlertEnabled ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-slate-300'
                  }`}>
                    Lost Affiliate Monitoring
                  </span>
                  <p className={`text-[10px] mt-0.5 ${
                    isFreePlan ? 'text-gray-400 dark:text-slate-500' : lostAlertEnabled ? 'text-emerald-600/70 dark:text-emerald-400/60' : 'text-gray-400 dark:text-slate-500'
                  }`}>
                    {isFreePlan ? 'Upgrade your plan to enable this feature' : lostAlertEnabled ? 'Checking weekly if affiliates still list your brand' : 'Get alerted when affiliates stop listing your brand'}
                  </p>
                </div>
              </div>
              {/* Toggle switch */}
              <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                isFreePlan ? 'bg-gray-200 dark:bg-slate-700' : lostAlertEnabled ? 'bg-emerald-500 dark:bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'
              }`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  !isFreePlan && lostAlertEnabled ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </div>
            </button>
          </div>
        )}

        {/* Lost Affiliates Dropdown - Show when monitoring is ON or lost affiliates exist */}
        {isCompleted && (lostAlertEnabled || (lostAffiliates && lostAffiliates.length > 0)) && (
          <div className="mt-3">
            <button
              onClick={handleLostAffiliatesToggle}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-red-50 to-amber-50 dark:from-slate-800 dark:to-slate-800 border border-red-200 dark:border-slate-600 rounded-lg hover:from-red-100 hover:to-amber-100 dark:hover:from-slate-700 dark:hover:to-slate-700 transition-all duration-200"
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />
                <span className="text-sm font-medium text-red-700 dark:text-red-300">Lost Affiliates</span>
                {lostAffiliates && lostAffiliates.length > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 rounded-full text-xs font-medium text-red-700 dark:text-red-400">
                    {lostAffiliates.length}
                  </span>
                )}
              </div>
              {lostAffiliatesOpen ? (
                <ChevronUp className="w-5 h-5 text-red-500 dark:text-red-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-red-500 dark:text-red-400" />
              )}
            </button>

            {lostAffiliatesOpen && (
              <div className="mt-3 border border-red-200 dark:border-slate-700 rounded-lg overflow-hidden">
                {lostAffiliatesLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
                    <span className="ml-2 text-sm text-gray-500 dark:text-slate-400">Loading...</span>
                  </div>
                ) : lostAffiliates && lostAffiliates.length > 0 ? (
                  <div className="divide-y divide-red-100 dark:divide-slate-700 max-h-60 overflow-y-auto">
                    {lostAffiliates.map((item, idx) => (
                      <div key={idx} className="px-4 py-3 hover:bg-red-50/50 dark:hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                              {item.affiliate_url?.substring(0, 2).toUpperCase()}
                            </div>
                            <a
                              href={`https://${item.affiliate_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-gray-700 dark:text-slate-200 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              onClick={e => e.stopPropagation()}
                            >
                              {item.affiliate_url}
                            </a>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-slate-500 whitespace-nowrap">
                            {new Date(item.detected_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[11px] text-red-600/70 dark:text-red-400/60 ml-7 truncate">
                          {item.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-slate-400">No lost affiliates detected yet</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">All affiliates are still listing your brand</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Monthly Analytics Dropdown Button - Only show if we have counts AND detailed data exists */}
        {!isProcessing && !isFailed && (gainedCount > 0 || lostCount > 0) && hasDetailedAnalytics && (
          <div className="mt-5">
            <button
              onClick={handleAnalyticsToggle}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 border border-blue-200 dark:border-slate-600 rounded-lg hover:from-blue-100 hover:to-indigo-100 dark:hover:from-slate-700 dark:hover:to-slate-700 transition-all duration-200"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Monthly Analytics</span>
                <div className="flex items-center gap-1.5 ml-2">
                  {gainedCount > 0 && (
                    <span className="flex items-center gap-0.5 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 rounded-full text-xs font-medium text-green-700 dark:text-green-400">
                      <TrendingUp className="w-3 h-3" />
                      {gainedCount}
                    </span>
                  )}
                  {lostCount > 0 && (
                    <span className="flex items-center gap-0.5 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 rounded-full text-xs font-medium text-red-700 dark:text-red-400">
                      <TrendingDown className="w-3 h-3" />
                      {lostCount}
                    </span>
                  )}
                </div>
              </div>
              {analyticsOpen ? (
                <ChevronUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              )}
            </button>

            {/* Analytics Dropdown Content */}
            {analyticsOpen && (
              <div className="mt-4 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Loading analytics...</span>
                  </div>
                ) : analyticsError ? (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 text-center">
                    <span className="text-sm text-red-600 dark:text-red-400">Failed to load analytics</span>
                  </div>
                ) : analyticsData && (analyticsData.data?.gained?.length > 0 || analyticsData.data?.lost?.length > 0) ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-gray-200 dark:divide-slate-700">
                    {/* Gained Affiliates */}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">
                          Affiliates Gained
                        </h4>
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 rounded-full text-xs font-medium text-green-700 dark:text-green-400">
                          {analyticsData.data?.gained?.length || 0}
                        </span>
                      </div>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {analyticsData.data?.gained && analyticsData.data.gained.length > 0 ? (
                          analyticsData.data.gained.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg"
                            >
                              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                {item.domain?.substring(0, 2).toUpperCase() || 'NA'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 dark:text-slate-200 truncate">
                                  {item.domain}
                                </p>
                              </div>
                              <span className="px-1.5 py-0.5 bg-green-200 dark:bg-green-800 rounded text-xs text-green-800 dark:text-green-300">
                                New
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">
                            No affiliates gained this month
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Lost Affiliates */}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">
                          Affiliates Lost
                        </h4>
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 rounded-full text-xs font-medium text-red-700 dark:text-red-400">
                          {analyticsData.data?.lost?.length || 0}
                        </span>
                      </div>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {analyticsData.data?.lost && analyticsData.data.lost.length > 0 ? (
                          analyticsData.data.lost.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg"
                            >
                              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                {item.domain?.substring(0, 2).toUpperCase() || 'NA'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 dark:text-slate-200 truncate">
                                  {item.domain}
                                </p>
                              </div>
                              <span className="px-1.5 py-0.5 bg-red-200 dark:bg-red-800 rounded text-xs text-red-800 dark:text-red-300">
                                Lost
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-4">
                            No affiliates lost this month
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-slate-800 text-center">
                    <span className="text-sm text-gray-500 dark:text-slate-400">No detailed analytics data available</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCard;